// Typed client for the ED Referral Letter (backend/routes/emergency/referral-letters.js).
import { api } from '@/lib/api/client';
import { getActiveBranchId, getAuthToken } from '@/lib/api/session';
import type { LetterPatient, ReferralFacts, TemplateKey } from './referralTemplates';

export interface Recipient {
  doctor_name?: string;
  qualification?: string;
  designation?: string;
  hospital?: string;
  city?: string;
}

export interface Signature {
  doctor_name?: string;
  qualification?: string;
  designation?: string;
  department?: string;
}

export type LetterStatus = 'draft' | 'final' | 'superseded';

export interface ReferralLetter {
  id: number;
  encounter_id: number;
  patient_id: number;
  visit_id: number;
  status: LetterStatus;
  template_key: TemplateKey;
  letter_date: string;
  recipient: Recipient;
  salutation: string;
  diagnosis: string | null;
  referral_reason: string | null;
  recommended_management: string | null;
  body: string;
  signature: Signature;
  version: number;
  amends_letter_id: number | null;
  amendment_reason: string | null;
  superseded_by_letter_id: number | null;
  document_id: number | null;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
  revision: number;
  finalized_by: string | null;
  finalized_at: string | null;
}

export interface Letterhead {
  name: string;
  address: string | null;
  phone: string | null;
  website: string | null;
  footer_text: string;
}

export interface ReferralPrefill {
  templates: Array<{ key: TemplateKey; label: string }>;
  letterhead: Letterhead;
  patient: LetterPatient & { name: string; title: string | null; uhid: string; er_number: string; visit_code: string };
  facts: ReferralFacts;
  defaults: {
    template_key: TemplateKey;
    letter_date: string;
    salutation: string;
    recipient: Recipient;
    diagnosis: string | null;
    referral_reason: string | null;
    recommended_management: string | null;
    signature: Signature;
  };
}

export type LetterInput = Pick<ReferralLetter,
  'template_key' | 'letter_date' | 'recipient' | 'salutation' | 'diagnosis' | 'referral_reason' | 'recommended_management' | 'body' | 'signature'>;

const enc = (id: number) => `/emergency/encounters/${id}/referral-letters`;
const one = (id: number) => `/emergency/referral-letters/${id}`;

export const fetchReferralLetters = (encounterId: number) =>
  api.get<{ letters: ReferralLetter[] }>(enc(encounterId)).then((r) => r.letters);
export const fetchReferralPrefill = (encounterId: number) => api.get<ReferralPrefill>(`${enc(encounterId)}/prefill`);
export const fetchReferralLetter = (id: number) => api.get<ReferralLetter>(one(id));
export const createReferralLetter = (encounterId: number, body: LetterInput) => api.post<ReferralLetter>(enc(encounterId), body);
export const saveReferralLetter = (id: number, body: LetterInput & { expected_revision: number }) =>
  api.put<ReferralLetter & { unchanged?: boolean }>(one(id), body);
export const discardReferralLetter = (id: number) => api.delete<void>(one(id));
export const finalizeReferralLetter = (id: number, expectedRevision: number) =>
  api.post<ReferralLetter & { uhid: string }>(`${one(id)}/finalize`, { expected_revision: expectedRevision });
export const amendReferralLetter = (id: number, reason: string) => api.post<ReferralLetter>(`${one(id)}/amend`, { reason });

/** The letter as a PDF blob (a draft prints with a DRAFT watermark). */
export async function fetchReferralPdf(id: number): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    const branchId = getActiveBranchId();
    if (branchId !== null) headers['X-Branch-Id'] = String(branchId);
  }
  const res = await fetch(`/api${one(id)}/pdf`, { headers });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `Could not load the letter (HTTP ${res.status})`);
  }
  return res.blob();
}
